import { LiveObject } from "@liveblocks/client";
import { useMap, useObject, useRoom } from "@liveblocks/react";
import { useAtom } from "jotai";
import { pickBy } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { LIVE_BLOCKS_MAP_KEYS } from "../consts";
import {
  blocksOpsRefAtom,
  blocksRefAtom,
  currentUserAtom,
} from "../jotai/atoms";
import {
  AnyBlock,
  defaultFeatureFlags,
  FeatureFlags,
  SpaceInfo,
  User,
} from "../types";
import { equalPosition } from "../utils/gridPosition";
import { nextUpdatedAt } from "../utils/updatedAt";
import { toObject, useMapWrap } from "./utils";

export function useSpaceInfo(): [
  SpaceInfo | undefined,
  LiveObject<SpaceInfo> | null
] {
  const client = useObject<SpaceInfo>(LIVE_BLOCKS_MAP_KEYS.SPACE_INFO);
  const map = client ? (client?.toObject?.() as SpaceInfo) : undefined;
  return [map, client];
}

// Don't use useMapWrapper here, because we want to be able to
// get just the specific blockId from the parent array.
export function useBlock(
  blockId: string
): [AnyBlock | undefined, LiveObject<AnyBlock> | undefined] {
  const client = useMap<string, LiveObject<AnyBlock>>("blocks");
  const room = useRoom();
  const _block = client?.get(blockId)?.toObject() as AnyBlock | undefined;
  const [block, setBlock] = useState<AnyBlock | undefined>(_block);

  useEffect(() => {
    function onChange() {
      setBlock(client?.get(blockId)?.toObject());
    }

    let unsubscribe: ReturnType<typeof room.subscribe>;
    if (client) {
      const block = client.get(blockId);
      if (block) {
        unsubscribe = room.subscribe(block, onChange);
      }
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [_block, client, blockId, room]);

  return [block, client?.get(blockId)];
}

export function useLiveBlockSubscription(
  liveBlock: LiveObject<AnyBlock>
): [AnyBlock | undefined, LiveObject<AnyBlock> | undefined] {
  const room = useRoom();
  const [block, setBlock] = useState<AnyBlock>(liveBlock?.toObject());

  useEffect(() => {
    const onChange = () => {
      setBlock(liveBlock.toObject());
    };

    const unsubscribe: ReturnType<typeof room.subscribe> = room.subscribe(
      liveBlock,
      onChange,
      { isDeep: true }
    );

    return () => {
      unsubscribe();
    };
  }, [liveBlock, room]);

  return [block, liveBlock];
}

export function useFeatureFlags(): [
  FeatureFlags | undefined,
  LiveObject<FeatureFlags> | null
] {
  const client = useObject<FeatureFlags>(LIVE_BLOCKS_MAP_KEYS.FLAGS);
  const map =
    client && Object.keys(client.toObject()).length > 0
      ? (client.toObject() as FeatureFlags)
      : defaultFeatureFlags;
  return [map, client];
}

export function useUserInfo(
  id: string | undefined | null
): [User | undefined, LiveObject<User> | undefined] {
  const room = useRoom();
  const client = useMap<string, LiveObject<User>>("users");
  const _user = (id ? client?.get(id)?.toObject() : undefined) as
    | User
    | undefined;
  const [user, setUser] = useState<User | undefined>(_user);

  useEffect(() => {
    if (!id) {
      return;
    }
    const onChange = () => {
      setUser(client?.get(id)?.toObject());
    };
    onChange();

    let unsubscribe: ReturnType<typeof room.subscribe>;
    if (client) {
      unsubscribe = room.subscribe(client, onChange, { isDeep: true });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [client, id, room]);

  if (!id) {
    return [undefined, undefined];
  }

  if (client && !client.get(id)) {
    client.set(id, new LiveObject<User>());
  }

  return [user, client?.get(id)];
}

export function useLiveUserSubscription(
  liveUser: LiveObject<User> | undefined
): [User | undefined, LiveObject<User> | undefined] {
  const [user, setUser] = useState<User | undefined>(liveUser?.toObject());
  const room = useRoom();

  useEffect(() => {
    if (!liveUser) {
      return;
    }

    const onChange = () => setUser(liveUser.toObject());
    const unsubscribe = room.subscribe(liveUser, onChange, { isDeep: true });
    return () => unsubscribe();
  }, [liveUser, room]);

  return [user, liveUser];
}

export function useUserInfos() {
  const users = useMapWrap<User["id"], User>("users", { deep: true });
  return users;
}

// Convenience handler for setting/updating block MapClients
export function updateBlockMap(
  client: LiveObject<AnyBlock>,
  updates: Partial<AnyBlock>,
  raise = true
) {
  // // Don't bother updating position with an equal value
  if (
    updates.position &&
    equalPosition(client.get("position"), updates.position)
  ) {
    delete updates.position;
  }
  const keys = Object.keys(updates) as Array<keyof Partial<AnyBlock>>;
  keys.forEach((key: keyof AnyBlock) => {
    const blockKey: keyof AnyBlock = key;
    if (updates[key] === undefined) {
      // setting undefined will not propogate through liveblocks
      // so we send null when we want to unset something
      client.set(blockKey, null);
    } else {
      client.set(blockKey, updates[key]);
    }
  });

  // updatedAt is mostly used to power zIndex values,
  if (raise) {
    client.set("updatedAt", nextUpdatedAt());
  }
}

export type UpsertBlock = (
  block: Partial<AnyBlock> | Partial<AnyBlock>[],
  raise?: boolean
) => void;

export function useBlocksRefSetup() {
  const room = useRoom();
  const mapClient = useMap<AnyBlock["id"], LiveObject<AnyBlock>>("blocks");
  const [blocksRef] = useAtom(blocksRefAtom);

  useEffect(() => {
    if (!mapClient) return;

    const setRef = () => {
      blocksRef.current = pickBy(
        toObject(mapClient),
        (block) => !block.deleted
      );
    };
    setRef();

    const unsubscribe = room.subscribe(mapClient, setRef, { isDeep: true });
    return unsubscribe;
  }, [mapClient, room, blocksRef]);
}

export function createBlockObj(
  data: Omit<AnyBlock, "id" | "updatedAt" | "madeBy">,
  madeBy?: User["id"]
): AnyBlock {
  return {
    ...data,
    id: uuidv4(),
    updatedAt: nextUpdatedAt(),
    madeBy,
  } as AnyBlock;
}

export function useBlocks() {
  const [blocks, blocksClient] = useMapWrap<AnyBlock["id"], AnyBlock>("blocks");
  const [currentUser] = useAtom(currentUserAtom);
  const [blocksOpsRef] = useAtom(blocksOpsRefAtom);

  const room = useRoom();

  const upsertBlock: UpsertBlock = useCallback(
    (block: Partial<AnyBlock> | Partial<AnyBlock>[], raise = false) => {
      if (!blocksClient) {
        throw new Error(
          "internal error: cannot write before liveblock client is ready"
        );
      }

      const blocks = Array.isArray(block) ? block : [block];

      room.batch(() => {
        for (let i = 0; i < blocks.length; i++) {
          let block = blocks[i];
          if (!block.id) {
            block.id = uuidv4();
          }

          let liveobj = blocksClient.get(block.id);

          if (!liveobj) {
            blocksClient.set(block.id, new LiveObject(block as AnyBlock));
            liveobj = blocksClient.get(block.id);
          }

          if (!liveobj) {
            continue;
          }

          // if you create or an edit a block that doesn't have an "owner", you're the owner now
          if (!liveobj.get("madeBy") && !block.madeBy) {
            block = { ...block, madeBy: currentUser?.id };
          }

          if (raise) {
            block = { ...block, updatedAt: nextUpdatedAt() };
          }

          updateBlockMap(liveobj, block, false);
        }
      });
    },
    [blocksClient, currentUser, room]
  );

  const deleteBlock = useCallback(
    (...ids: string[]) => {
      if (!blocksClient) return;

      room.batch(() => {
        for (const id of ids) {
          const block = blocksClient?.get(id);
          block?.set("deleted", true);
        }
      });
    },
    [blocksClient, room]
  );

  blocksOpsRef.current = { upsertBlock, deleteBlock };
  const isReady = !!blocksClient;

  return { upsertBlock, deleteBlock, blocks, clients: blocksClient, isReady };
}

export function useBlocksData(opts?: { deep: boolean }) {
  const [blocks] = useMapWrap<AnyBlock["id"], AnyBlock>("blocks", opts);
  return blocks;
}
